import {computed, ComputedRef, inject, InjectionKey, nextTick, onScopeDispose, provide, shallowRef} from 'vue'
import {createEventHook, EventHookOn} from '@vueuse/core'

export interface IScaleAnimationContext {
  isAnimating: ComputedRef<boolean>
  onAnimationEnd: EventHookOn<void>
}

export interface IScaleAnimationContextProvider extends IScaleAnimationContext {
  startAnimation: (element: Element) => void
  endAnimation: (element: Element) => void
  notifyAnimationEnd: () => void
}

const scaleAnimationContextKey: InjectionKey<IScaleAnimationContext> = Symbol('ScaleAnimationContext')
const ANIMATION_WATCHDOG_MS = 2000

const idleScaleAnimationContext: IScaleAnimationContext = {
  isAnimating: computed(() => false),
  onAnimationEnd: () => ({
    off: () => undefined,
  }),
}

function buildContext(): IScaleAnimationContextProvider {
  const animatingElements = shallowRef<ReadonlySet<Element>>(new Set())
  const watchdogTimeoutByElement = new Map<Element, ReturnType<typeof setTimeout>>()
  const animationEndHook = createEventHook<void>()

  function endAnimation(element: Element): void {
    clearTimeout(watchdogTimeoutByElement.get(element))
    watchdogTimeoutByElement.delete(element)
    if(!animatingElements.value.has(element)) {
      return
    }
    const nextAnimatingElements = new Set(animatingElements.value)
    nextAnimatingElements.delete(element)
    animatingElements.value = nextAnimatingElements
  }

  function startAnimation(element: Element): void {
    clearTimeout(watchdogTimeoutByElement.get(element))
    watchdogTimeoutByElement.set(element, setTimeout(() => endAnimation(element), ANIMATION_WATCHDOG_MS))
    if(animatingElements.value.has(element)) {
      return
    }
    animatingElements.value = new Set(animatingElements.value).add(element)
  }

  function notifyAnimationEnd(): void {
    nextTick(() => {
      requestAnimationFrame(() => {
        animationEndHook.trigger()
      })
    })
  }

  onScopeDispose(() => {
    for(const timeoutId of watchdogTimeoutByElement.values()) {
      clearTimeout(timeoutId)
    }
    watchdogTimeoutByElement.clear()
  })

  return {
    isAnimating: computed(() => animatingElements.value.size > 0),
    onAnimationEnd: animationEndHook.on,
    startAnimation,
    endAnimation,
    notifyAnimationEnd,
  }
}

export function useProvideScaleAnimationContext(): IScaleAnimationContextProvider {
  const context = buildContext()
  provide(scaleAnimationContextKey, context)
  return context
}

export function useScaleAnimationContext(): IScaleAnimationContext {
  return inject(scaleAnimationContextKey, idleScaleAnimationContext)
}
